import { useMemo } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import LinearProgress from '@mui/material/LinearProgress'
import Grid from '@mui/material/Unstable_Grid2'
// eslint-disable-next-line import/no-duplicates
import { format } from 'date-fns'
// eslint-disable-next-line import/no-duplicates
import ptBR from 'date-fns/locale/pt-BR'

import { masks } from 'src/utils/masks'
import { fNumber, fPercent } from 'src/utils/format-number'
import Iconify from 'src/components/iconify'

import { SellerRow } from './seller-table'

const PAYMENT_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  money: { label: 'Dinheiro', icon: 'solar:dollar-minimalistic-bold', color: '#16a34a' },
  credit: { label: 'Cartão de Crédito', icon: 'solar:card-bold', color: '#2563eb' },
  debit: { label: 'Cartão de Débito', icon: 'solar:card-2-bold', color: '#0ea5e9' },
  pix: { label: 'Pix', icon: 'simple-icons:pix', color: '#06b6d4' },
  card: { label: 'CashLess', icon: 'solar:wallet-money-bold', color: '#a855f7' },
}

type Props = {
  open: boolean
  onClose: () => void
  seller: SellerRow | null
  totalSalesAll: number
}

export default function SellerDetailDialog({ open, onClose, seller, totalSalesAll }: Props) {
  const share = useMemo(() => {
    if (!seller || !totalSalesAll) return 0
    return (seller.totalSales / totalSalesAll) * 100
  }, [seller, totalSalesAll])

  const paymentEntries = useMemo(() => {
    if (!seller?.paymentTypeStats) return []
    const total = seller.totalSales || 1
    return Object.entries(seller.paymentTypeStats)
      .map(([type, stats]) => ({
        type,
        totalAmount: stats.totalAmount,
        count: stats.count,
        share: (stats.totalAmount / total) * 100,
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }, [seller])

  const activeDuration = useMemo(() => {
    if (!seller?.firstSale || !seller?.lastSale) return null
    const first = new Date(seller.firstSale).getTime()
    const last = new Date(seller.lastSale).getTime()
    const diffMs = Math.max(0, last - first)
    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    if (!hours && !minutes) return '—'
    return `${hours}h ${minutes}min`
  }, [seller])

  const ordersPerHour = useMemo(() => {
    if (!seller?.firstSale || !seller?.lastSale || !seller.totalOrders) return null
    const first = new Date(seller.firstSale).getTime()
    const last = new Date(seller.lastSale).getTime()
    const diffH = Math.max(1, (last - first) / (1000 * 60 * 60))
    return seller.totalOrders / diffH
  }, [seller])

  if (!seller) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      {/* HEADER */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
            size='small'
          >
            <Iconify icon='eva:close-fill' width={18} />
          </IconButton>
          <Stack direction='row' alignItems='center' spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon='solar:user-bold' width={26} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='h6' fontWeight={700} sx={{ fontSize: 18 }} noWrap>
                {seller.sellerName}
              </Typography>
              {seller.sellerEmail ? (
                <Typography variant='caption' sx={{ opacity: 0.9, fontSize: 12 }}>
                  {seller.sellerEmail}
                </Typography>
              ) : null}
              <Stack direction='row' spacing={1} sx={{ mt: 0.75 }}>
                <Chip
                  label={`${fPercent(share)} do evento`}
                  size='small'
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                  }}
                />
                <Chip
                  label={`${fNumber(seller.totalOrders)} pedidos`}
                  size='small'
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: 'background.neutral' }}>
        <Stack spacing={2}>
          {/* KPI MINI CARDS */}
          <Grid container spacing={1.25}>
            <MiniStat
              icon='solar:dollar-minimalistic-bold'
              color='#10b981'
              label='Faturamento'
              value={masks.currencyAllPlatforms(seller.totalSales)}
            />
            <MiniStat
              icon='solar:check-circle-bold'
              color='#3b82f6'
              label='Líquido'
              value={masks.currencyAllPlatforms(seller.netSales)}
              hint={`-${masks.currencyAllPlatforms(seller.totalTax || 0)} taxas`}
            />
            <MiniStat
              icon='solar:cart-large-2-bold'
              color='#8b5cf6'
              label='Ticket Médio'
              value={masks.currencyAllPlatforms(seller.averageTicket)}
            />
            <MiniStat
              icon='solar:box-bold'
              color='#f59e0b'
              label='Itens vendidos'
              value={fNumber(seller.totalItems || 0)}
              hint={
                seller.uniqueProductsCount
                  ? `${fNumber(seller.uniqueProductsCount)} produtos diferentes`
                  : undefined
              }
            />
          </Grid>

          {/* ATIVIDADE */}
          {(seller.firstSale || seller.lastSale) && (
            <Paper sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.25 }}>
                <Iconify icon='solar:clock-circle-bold' width={18} sx={{ color: 'info.main' }} />
                <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                  Atividade
                </Typography>
              </Stack>
              <Grid container spacing={1.5}>
                <Grid xs={6} sm={3}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                    1ª venda
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: 12.5 }}>
                    {seller.firstSale
                      ? format(new Date(seller.firstSale), 'dd/MM HH:mm', { locale: ptBR })
                      : '—'}
                  </Typography>
                </Grid>
                <Grid xs={6} sm={3}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                    Última venda
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: 12.5 }}>
                    {seller.lastSale
                      ? format(new Date(seller.lastSale), 'dd/MM HH:mm', { locale: ptBR })
                      : '—'}
                  </Typography>
                </Grid>
                <Grid xs={6} sm={3}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                    Tempo ativo
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: 12.5 }}>
                    {activeDuration || '—'}
                  </Typography>
                </Grid>
                <Grid xs={6} sm={3}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                    Pedidos/hora
                  </Typography>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: 12.5 }}>
                    {ordersPerHour ? ordersPerHour.toFixed(1) : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* PAGAMENTOS */}
          {paymentEntries.length > 0 && (
            <Paper sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1.5 }}>
                <Iconify icon='solar:card-bold' width={18} sx={{ color: 'primary.main' }} />
                <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                  Distribuição por Pagamento
                </Typography>
              </Stack>
              <Stack spacing={1.25}>
                {paymentEntries.map((p) => {
                  const meta = PAYMENT_LABELS[p.type] || {
                    label: p.type,
                    icon: 'solar:wallet-bold',
                    color: '#64748b',
                  }
                  return (
                    <Box key={p.type}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        spacing={1}
                        sx={{ mb: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${meta.color}22`,
                            color: meta.color,
                          }}
                        >
                          <Iconify icon={meta.icon} width={13} />
                        </Box>
                        <Typography
                          variant='body2'
                          fontWeight={600}
                          sx={{ fontSize: 12.5, flex: 1 }}
                        >
                          {meta.label}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
                          {fNumber(p.count)} ped.
                        </Typography>
                        <Typography variant='body2' fontWeight={700} sx={{ fontSize: 12.5 }}>
                          {masks.currencyAllPlatforms(p.totalAmount)}
                        </Typography>
                        <Box
                          sx={{
                            minWidth: 52,
                            textAlign: 'right',
                            color: meta.color,
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {fPercent(p.share)}
                        </Box>
                      </Stack>
                      <LinearProgress
                        variant='determinate'
                        value={Math.min(p.share, 100)}
                        sx={{
                          height: 5,
                          borderRadius: 2,
                          bgcolor: 'divider',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: meta.color,
                          },
                        }}
                      />
                    </Box>
                  )
                })}
              </Stack>
            </Paper>
          )}

          {/* TOP PRODUTOS */}
          {seller.topProducts && seller.topProducts.length > 0 && (
            <Paper sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ mb: 1.5 }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Iconify icon='solar:cup-star-bold' width={18} sx={{ color: 'warning.main' }} />
                  <Typography variant='subtitle2' fontWeight={700} sx={{ fontSize: 13 }}>
                    Top {seller.topProducts.length} Produtos
                  </Typography>
                </Stack>
                {seller.uniqueProductsCount ? (
                  <Chip
                    label={`${seller.uniqueProductsCount} produtos no total`}
                    size='small'
                    variant='soft'
                    color='info'
                    sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                  />
                ) : null}
              </Stack>

              <Stack divider={<Divider flexItem />} spacing={0}>
                {seller.topProducts.map((p, idx) => (
                  <Stack
                    key={`${p.productName}-${idx}`}
                    direction='row'
                    alignItems='center'
                    spacing={1.5}
                    sx={{ py: 1 }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: idx === 0 ? 'warning.main' : 'grey.300',
                        color: idx === 0 ? 'white' : 'text.primary',
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{ fontSize: 12.5 }}
                        noWrap
                      >
                        {p.productName}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ fontSize: 11 }}
                      >
                        {p.categoryName}
                      </Typography>
                    </Box>
                    <Stack alignItems='flex-end' spacing={0}>
                      <Typography variant='body2' fontWeight={700} sx={{ fontSize: 12.5 }}>
                        {fNumber(p.totalQuantity)} un.
                      </Typography>
                      <Typography
                        variant='caption'
                        color='success.main'
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      >
                        {masks.currencyAllPlatforms(p.totalRevenue)}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

// ----------------------------------------------------------------------

function MiniStat({
  icon,
  color,
  label,
  value,
  hint,
}: {
  icon: string
  color: string
  label: string
  value: string
  hint?: string
}) {
  return (
    <Grid xs={6} sm={3}>
      <Paper
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          height: '100%',
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}22`,
              color,
            }}
          >
            <Iconify icon={icon} width={12} />
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10.5 }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant='body2' fontWeight={700} sx={{ fontSize: 14, lineHeight: 1.25 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10.5 }}>
            {hint}
          </Typography>
        ) : null}
      </Paper>
    </Grid>
  )
}
